import { computed, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import usePrompts from '@/composables/usePrompts'
import { parseEntityResponse } from '@/shared/utils/workflowSpec'
import { SchemaType } from '@/constants/schema'
import { runAiGeneration } from '@/utils/helpHero'

const useGenerateEntity = () => {
  const store = useStore()
  const { proxy } = getCurrentInstance()
  const workflow = computed(() => store.state.workflow.workflow)

  const { getSingleEntity } = usePrompts()

  // Its saves are the AI's, not the person's own edits (activation tracking).
  const generateEntity = (params) => runAiGeneration(() => createEntity(params))

  const createEntity = async ({ name, description }) => {
    let newEntities

    if (!workflow.value.aiDescription) {
      await store.dispatch('workflow/openWorkflowDescriptionDialog')
      return
    }

    proxy.$loadingState.start()

    try {
      const res = await getSingleEntity(name, description)

      if (!res?.entity) {
        store.commit('snackbar/showMessage', {
          content: 'The AI was unable to create an entity. Please try adjusting the description.',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        proxy.$loadingState.stop()
        return
      }

      const entityNameToIdMap = new Map()
      for (const schema of workflow.value.schemas || []) {
        if (schema.type === SchemaType.ENTITY) {
          entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
        }
      }

      const entityTable = parseEntityResponse(res.entity, entityNameToIdMap)

      // Slot the entity into an existing bounded context if the AI named one that
      // matches; for a single entity we never create a new bounded context.
      const bcName = res.entity.boundedContext?.trim().toLowerCase()
      if (bcName) {
        const bc = (workflow.value.boundedContexts ?? []).find((b) => b.name?.trim().toLowerCase() === bcName)
        if (bc) {
          entityTable.boundedContext = bc.id
        }
      }

      const entityExists = (workflow.value.schemas ?? []).some((item) => item.name === entityTable.name)

      if (entityTable && !entityExists) {
        newEntities = [...safeStructuredClone(workflow.value.schemas ?? []), entityTable]
      }

      if (newEntities?.length > 0) {
        await workflowApi.updateSchemas({
          schemas: newEntities,
          workflow: workflow.value
        })
      }

      proxy.$loadingState.stop()

      return entityTable
    } catch (error) {
      if (error.name !== 'AbortError') {
        await store.dispatch('workflow/openRetryDialog', {
          failedItems: [{ type: 'entity', name: 'Generate Entity' }],
          retryCallback: () => generateEntity({ name, description })
        })
      }
      proxy.$loadingState.stop()
    }
  }

  return { generateEntity }
}

export default useGenerateEntity
